/**
 * 全編モーショングラフィックス (Phase 2e・docs/features/brand-cut-motion-graphics-plan.md §10.5) の表示・判定用の純関数。
 *
 * - engine 判定: 映像スタイル option の metadata.production_engine === 'motion' (DB のマイグレーション適用前は付かない = 従来どおり)
 * - 生成ボタン直後の BGM モーダルの料金 (シナリオ + BGM)
 * - BGM 確認画面の段階 (BGM 生成中 / 失敗 / 確認待ち / 台本生成中)
 * - 計画 (motion_plan) の advice・replan の変更一覧の整形
 *
 * 正本の判定は backend (scenario_motion_prepare.py / motion_plan.py)。ここは画面の出し分けだけを持つ。
 */
import type { HierarchicalOption } from '../../common/HierarchicalSelect';
import { findHierarchicalOptionMetadata } from './lyricsDurationGate';
import { MOTION_GRAPHICS_WHOLE, effectiveMotionGraphics } from '../state/motionGraphics';
import type {
  BgmProvider,
  MotionPlanAdvice,
  MotionWholeInfo,
  MotionWholeJobResult,
  MotionPlanChange,
  MotionPlanItem,
  MotionPlanResponse,
  MotionSongSummary,
} from '../../../features/scenarios/api';
import type { MotionSyncStatus } from '../../../features/scenarios/types';
import { motionVoiceStaleMessage } from './voiceLines';

export const MOTION_ENGINE = 'motion';

/** 映像スタイル key が全編モーション (option の metadata.production_engine === 'motion') か。
 *  移行前のツリーだけが該当する (migration 20261015_02 で option が「モーショングラフィックス」の whole へ付け替わる・§10.14)。 */
export function isMotionEngineVisualStyle(options: HierarchicalOption[], visualStyleKey: string | null | undefined): boolean {
  if (!visualStyleKey) return false;
  return findHierarchicalOptionMetadata(options, visualStyleKey)?.production_engine === MOTION_ENGINE;
}

/**
 * 全編モーションか (§10.14): 「モーショングラフィックス」の3択が whole (ツリーで選べる値のとき)、または
 * 移行前の映像スタイル option の metadata.production_engine === 'motion'。BGM先行モーダル・歌モノ / 60秒超の拒否・
 * 案数1固定・台本取り込みの非活性などの判定はすべてこの値で行う。backend の resolve_motion_graphics と同じ規則。
 */
export function resolveMotionEngine(params: {
  motionGraphics: string;
  motionGraphicsOptions: HierarchicalOption[];
  visualStyleOptions: HierarchicalOption[];
  visualStyle: string | null | undefined;
}): boolean {
  if (effectiveMotionGraphics(params.motionGraphics, params.motionGraphicsOptions) === MOTION_GRAPHICS_WHOLE) return true;
  return isMotionEngineVisualStyle(params.visualStyleOptions, params.visualStyle);
}

type ScenarioMetaSource = {
  status?: string | null;
  generation_metadata?: Record<string, unknown> | null;
};

function metaOf(scenario: ScenarioMetaSource | null | undefined): Record<string, unknown> {
  const meta = scenario?.generation_metadata;
  return meta && typeof meta === 'object' ? meta : {};
}

/** 全編モーションで作ったシナリオ (generation_metadata.production_engine === 'motion') か。 */
export function isMotionEngineScenario(scenario: ScenarioMetaSource | null | undefined): boolean {
  return metaOf(scenario).production_engine === MOTION_ENGINE;
}

/** シェルの generation_metadata.motion_prepare の要約 (BGM 先行フローの進み具合)。無ければ null。 */
export interface MotionPrepareInfo {
  bgmMode: 'generate' | 'upload' | 'none' | null;
  autoContinue: boolean;
  scenarioJobId: string | null;
  /** 解析済みの曲の要約。undefined = まだ解析していない / null = 拍が取れない (秒ベース) */
  song: MotionSongSummary | null | undefined;
  /** 解析サービスの失敗の理由 (拍が取れない曲とは別。復旧後に「尺の割付を更新」で合わせ直せる) */
  songError: string | null;
}

export function readMotionPrepare(scenario: ScenarioMetaSource | null | undefined): MotionPrepareInfo | null {
  const prep = metaOf(scenario).motion_prepare;
  if (!prep || typeof prep !== 'object') return null;
  const p = prep as Record<string, unknown>;
  const bgmMode = p.bgm_mode === 'generate' || p.bgm_mode === 'upload' || p.bgm_mode === 'none' ? p.bgm_mode : null;
  return {
    bgmMode,
    autoContinue: p.auto_continue !== false,
    scenarioJobId: typeof p.scenario_job_id === 'string' && p.scenario_job_id ? p.scenario_job_id : null,
    song: 'song' in p ? (p.song as MotionSongSummary | null) : undefined,
    songError: typeof p.song_error === 'string' && p.song_error ? p.song_error : null,
  };
}

/** 全編モーションの BGM 先行フローで、まだ本文が無いシェル (status='generating' + motion_prepare) か。 */
export function isMotionPrepareShell(scenario: ScenarioMetaSource | null | undefined): boolean {
  return scenario?.status === 'generating' && readMotionPrepare(scenario) !== null;
}

/**
 * 映像スタイルがモーションのときのトーンの選択肢: 歌モノ (metadata.bgm_mode === 'lyrics') を外す (§10.5「一体化しない」)。
 * 既に選ばれている歌モノは表示名が消えないように残す (呼び出し側が警告を出して送らせない)。空になった group も外す。
 */
export function filterLyricsTonesForMotion(options: HierarchicalOption[], currentTone: string | null | undefined): HierarchicalOption[] {
  const out: HierarchicalOption[] = [];
  for (const opt of options) {
    if (opt.children && opt.children.length > 0) {
      const children = filterLyricsTonesForMotion(opt.children, currentTone);
      if (children.length > 0) out.push({ ...opt, children });
      continue;
    }
    if (opt.metadata?.bgm_mode === 'lyrics' && opt.key !== currentTone) continue;
    out.push(opt);
  }
  return out;
}

/** 全編モーションの BGM の尺に足す余白 (秒)。backend の MOTION_BGM_TAIL_SECONDS と同じ値 (2026-10-02 に 3 → 0) */
export const MOTION_BGM_TAIL_SECONDS = 0;

/** 全編モーションの BGM の尺 = 想定尺 + 余白 (3〜600)。想定尺が無ければ 30 秒 + 余白 (backend motion_bgm_duration_seconds と同じ)。 */
export function motionBgmDurationSeconds(expectedSeconds: number | null | undefined): number {
  const base = typeof expectedSeconds === 'number' && Number.isFinite(expectedSeconds) && expectedSeconds > 0
    ? Math.floor(expectedSeconds)
    : 30;
  return Math.max(3, Math.min(600, base + MOTION_BGM_TAIL_SECONDS));
}

const BGM_RESOURCE_TYPE_BY_PROVIDER: Record<BgmProvider, string> = {
  elevenlabs: 'elevenlabs-music',
  lyria: 'lyria-clip-music',
  'lyria-pro': 'lyria-pro-music',
};

type CreditRateRow = { service_type: string; resource_type: string; credits_per_unit: number };

/**
 * BGM 生成のポイント (backend scenario_motion_prepare._bgm_credits_required と同じ規則)。
 * Lyria 系は 1 曲固定、ElevenLabs は 1 分あたりのレートを秒で按分して切り上げ。レートが無ければ null。
 */
export function motionBgmCredits(
  rates: readonly CreditRateRow[] | null | undefined,
  provider: BgmProvider,
  seconds: number,
): number | null {
  const rate = rates?.find((r) => r.service_type === 'bgm' && r.resource_type === BGM_RESOURCE_TYPE_BY_PROVIDER[provider]);
  if (!rate) return null;
  const perUnit = Number(rate.credits_per_unit);
  if (!Number.isFinite(perUnit)) return null;
  if (provider === 'lyria' || provider === 'lyria-pro') return Math.max(1, Math.ceil(perUnit));
  return Math.max(1, Math.ceil(perUnit * seconds / 60));
}

/** モーションシーン 1 本の定額 (credit_rate_config の video_generation / motion-graphics-scene)。無ければ null。 */
export function motionSceneCreditRate(rates: readonly CreditRateRow[] | null | undefined): number | null {
  const rate = rates?.find((r) => r.service_type === 'video_generation' && r.resource_type === 'motion-graphics-scene');
  if (!rate) return null;
  const v = Number(rate.credits_per_unit);
  return Number.isFinite(v) ? v : null;
}

export interface CreditRange { min: number; max: number }

export function formatCreditRange(range: CreditRange): string {
  return range.min === range.max ? `${range.min}` : `${range.min}~${range.max}`;
}

/**
 * モーダルに出す合計 = シナリオ (ランダムは 1 モデル分のレンジ) + BGM (生成するときだけ)。
 * シナリオの料金が分からなければ null (表示しない)。BGM の料金が分からないときはシナリオだけを返し bgmUnknown を立てる。
 */
export function combineMotionPrepareCredits(params: {
  scenario: CreditRange | null;
  bgm: number | null;
  generateBgm: boolean;
}): { total: CreditRange; bgmUnknown: boolean } | null {
  const { scenario, bgm, generateBgm } = params;
  if (!scenario) return null;
  if (!generateBgm) return { total: scenario, bgmUnknown: false };
  if (bgm == null) return { total: scenario, bgmUnknown: true };
  return { total: { min: scenario.min + bgm, max: scenario.max + bgm }, bgmUnknown: false };
}

function fmtSec(sec: number): string {
  const rounded = Math.round(sec * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/** 曲の要約の 1 行。null は秒ベースで書く旨 (songError があれば解析サービスの失敗として区別し、復旧後の「尺の割付を更新」に誘導)。 */
export function formatMotionSongSummary(song: MotionSongSummary | null | undefined, songError?: string | null): string {
  if (!song && songError) return `BGM の解析ができませんでした (${songError})。台本は秒ベースで書きます。解析サービスの復旧後に「尺の割付を更新」で曲に合わせ直せます`;
  if (!song) return '拍が取れない曲なので、台本は秒ベースで書きます';
  const bpm = song.bpm != null ? `${Math.round(song.bpm)} BPM・` : '';
  const grid = song.gridKind === 'beats' ? ' (小節頭が取れないため 4 拍ごとに区切ります)' : '';
  if (song.capSec != null && song.songBarCount != null && song.songBarCount > song.barCount) {
    // 曲が想定尺より長い: 台本は先頭 capSec 秒ぶんの小節で書き、残りはフェードアウトで切る (backend motion_song.cap_for_duration)
    return `${bpm}曲 ${fmtSec(song.songSec)} 秒のうち先頭 ${fmtSec(song.capSec)} 秒ぶんの ${song.barCount} 小節 (1 小節 ${fmtSec(song.barSec)} 秒) に合わせて台本を書きます。残りはフェードアウトで切ります${grid}`;
  }
  return `${bpm}${song.barCount} 小節 (1 小節 ${fmtSec(song.barSec)} 秒・曲 ${fmtSec(song.songSec)} 秒) に合わせて台本を書きます${grid}`;
}

/** 曲に収まらないときの advice → 見出し + シーンごとの行 (「シーン 2: ナレーション 10 秒 → 3 秒縮める」)。 */
export function formatMotionAdvice(advice: MotionPlanAdvice): { message: string; lines: string[] } {
  const lines = [...(advice.scenes || [])]
    .filter((s) => s.shortenSec > 0)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((s) => `シーン ${s.order ?? '?'}: ナレーション ${fmtSec(s.narrationSec)} 秒 → ${fmtSec(s.shortenSec)} 秒縮める`);
  if (advice.extendBgmSec > 0) lines.push(`または BGM を ${fmtSec(advice.extendBgmSec)} 秒長くする`);
  return { message: advice.message, lines };
}

/** replan の変更一覧 → 「シーン 2: 8 秒 → 9 秒」(order 順)。 */
export function formatMotionPlanChanges(changed: readonly MotionPlanChange[]): string[] {
  return [...changed]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((c) => `シーン ${c.order ?? '?'}: ${c.before == null ? '未設定' : `${fmtSec(c.before)} 秒`} → ${fmtSec(c.after)} 秒`);
}

/** replan の変更 → 画面の予定尺 (scenes.video_duration_seconds は整数で保存される) のパッチ。 */
export function motionPlanDurationPatch(changed: readonly MotionPlanChange[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const c of changed) {
    if (typeof c.after === 'number' && Number.isFinite(c.after)) out.set(c.sceneId, Math.round(c.after));
  }
  return out;
}

/** BGM 確認画面の段階。 */
export type MotionPrepareStage = 'bgm_generating' | 'bgm_failed' | 'confirm' | 'scenario_generating';

/** BGM ジョブの見えている状態 (GET /jobs/<id>)。ジョブ id を知らない (再読み込み後) ときは null。 */
export interface MotionBgmJobView {
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  scenarioJobId?: string | null;
}

/**
 * 確認画面の段階を決める。
 * - シナリオ生成ジョブが載っていれば (シェルの記録・BGM ジョブの結果・この画面で投入) 台本生成中
 * - BGM を生成しない (upload / none) なら常に台本生成中 (prepare がジョブを載せている)
 * - BGM ジョブが見えていれば、その状態 (完了して続行していない = 確認待ち)
 * - 見えていない (再読み込み後) なら、BGM が紐づいていれば確認待ち・無ければ生成中とみなす
 */
export function deriveMotionPrepareStage(input: {
  bgmMode: MotionPrepareInfo['bgmMode'];
  scenarioJobId: string | null | undefined;
  bgmJob: MotionBgmJobView | null | undefined;
  bgmMaterialId: string | null | undefined;
  bgmExcluded: boolean | null | undefined;
}): MotionPrepareStage {
  if (input.scenarioJobId || input.bgmJob?.scenarioJobId) return 'scenario_generating';
  if (input.bgmMode && input.bgmMode !== 'generate') return 'scenario_generating';
  const job = input.bgmJob;
  if (job) {
    if (job.status === 'failed' || job.status === 'cancelled') return 'bgm_failed';
    if (job.status === 'completed') return 'confirm';
    return 'bgm_generating';
  }
  if (input.bgmMaterialId && !input.bgmExcluded) return 'confirm';
  return 'bgm_generating';
}

type SceneForCount = {
  brand_cut_position?: string | null;
  video_input_mode?: string | null;
  video_material_id?: string | null;
  video_excluded?: boolean | null;
};

/** 未生成 (採用動画なし or 除外) の本編モーションシーンの数 (全編を生成の確認で「置き換わる」注意を出すかの判定)。 */
export function countPendingMotionScenes(scenes: readonly SceneForCount[] | null | undefined): number {
  return (scenes || []).filter((s) => !s.brand_cut_position
    && s.video_input_mode === MOTION_ENGINE
    && (!s.video_material_id || !!s.video_excluded)).length;
}

/** 計画にブランドカットのシーンの item があればそれ (計画の尺 sec と scene_id をブランドカット生成に送る)。 */
export function findMotionBrandCutPlanItem(
  plan: MotionPlanResponse | null | undefined,
  sceneId: string | null | undefined,
): MotionPlanItem | null {
  if (!plan || !sceneId) return null;
  const mp = plan.motion_plan;
  if (!mp || mp.planned !== true) return null;
  const item = mp.items.find((it) => it.sceneId === sceneId)
    ?? plan.scenes.find((s) => s.scene_id === sceneId)?.item
    ?? null;
  if (!item || (item.kind !== 'brand_start' && item.kind !== 'brand_end')) return null;
  return item;
}

/** sync-status の reason が「尺の割付が古い: …」か (planStale の旗が無い古い応答も文言で拾う)。 */
export function isPlanStaleSyncStatus(status: MotionSyncStatus | null | undefined): boolean {
  if (!status?.stale) return false;
  return status.planStale === true || (status.reason ?? '').startsWith('尺の割付が古い');
}

/** シーンの同期の注意文。尺の割付が古いときは backend の文言をそのまま出す (§10.5)。 */
export function motionSyncNoticeText(status: MotionSyncStatus | null | undefined): string | undefined {
  if (!status?.stale) return undefined;
  if (isPlanStaleSyncStatus(status) && status.reason) return status.reason;
  return `BGM との同期が古い可能性があります${status.reason ? ` (${status.reason})` : ''}。再生成で合わせ直せます`;
}

// ── BGM ジョブ id の控え (再読み込みしても確認画面が BGM ジョブを追えるように・タブ単位) ──
const BGM_JOB_STORAGE_PREFIX = 'motion_prepare_bgm_job:';

export function rememberMotionBgmJob(scenarioId: string, jobId: string): void {
  try { sessionStorage.setItem(`${BGM_JOB_STORAGE_PREFIX}${scenarioId}`, jobId); } catch { /* ignore */ }
}

export function recallMotionBgmJob(scenarioId: string): string | null {
  try { return sessionStorage.getItem(`${BGM_JOB_STORAGE_PREFIX}${scenarioId}`); } catch { return null; }
}

export function forgetMotionBgmJob(scenarioId: string): void {
  try { sessionStorage.removeItem(`${BGM_JOB_STORAGE_PREFIX}${scenarioId}`); } catch { /* ignore */ }
}

/** 計画の状態としてパネルに出す注意。null = 出さない。 */
export type MotionPlanNotice =
  | { kind: 'advice'; message: string; lines: string[] }
  | { kind: 'stale'; reason: string }
  | { kind: 'fallback'; reason: string }
  | { kind: 'relaxed'; message: string };

/**
 * GET /motion/plan の結果 → パネルの注意。
 * 「尺の割付が古い」は生成済みシーンの注意文 (sync-status) と重複させない: どれかの生成済みシーンが「尺の割付が古い」を
 * 出しているならパネルでは出さない (「尺の割付を更新」の操作だけ残す)。
 */
export function deriveMotionPlanNotice(
  plan: MotionPlanResponse | null | undefined,
  sceneStatuses: Readonly<Record<string, MotionSyncStatus>> | null | undefined,
): MotionPlanNotice | null {
  if (!plan) return null;
  const mp = plan.motion_plan;
  if (mp && mp.planned === false) {
    if (mp.advice) {
      const { message, lines } = formatMotionAdvice(mp.advice);
      return { kind: 'advice', message: message || mp.reason, lines };
    }
    if (mp.reason) return { kind: 'fallback', reason: mp.reason };
    return null;
  }
  if (plan.check?.hasPlan && plan.check.matches === false) {
    const sceneShowsIt = Object.values(sceneStatuses || {}).some((s) => isPlanStaleSyncStatus(s));
    if (sceneShowsIt) return null;
    return { kind: 'stale', reason: plan.check.reason || '尺を割り付けた後にシナリオか BGM が変わりました' };
  }
  // 小節に合わせきれず拍 / 秒に緩めた・曲の後ろへはみ出した (生成は止めない・注意だけ)
  if (mp && mp.planned === true && mp.relaxNotice) return { kind: 'relaxed', message: mp.relaxNotice };
  return null;
}

// ── 全編を 1 本で生成して計画の境界で分割する (Phase 2f・docs/features/brand-cut-motion-graphics-plan.md §10.6) ──

/** 全編を 1 回の描画で作れる総尺の上限 (秒)。backend / レンダラーの MOTION_MAX_DURATION_SEC と同じ */
export const MOTION_WHOLE_MAX_SEC = 60;
/** 全編の「AI で修正」の指示の最大文字数 (backend INSTRUCTION_MAX_CHARS と同じ) */
export const MOTION_REVISE_INSTRUCTION_MAX = 500;

/** 全編の「AI で修正」の定額 (credit_rate_config の video_generation / motion-graphics-brand-cut-revise を共用)。無ければ null。 */
export function motionReviseCreditRate(rates: readonly CreditRateRow[] | null | undefined): number | null {
  const rate = rates?.find((r) => r.service_type === 'video_generation' && r.resource_type === 'motion-graphics-brand-cut-revise');
  if (!rate) return null;
  const v = Number(rate.credits_per_unit);
  return Number.isFinite(v) ? v : null;
}

/** 計画に載る本編シーンの数 (ブランドカットは数えない = 料金の N)。 */
export function countMotionBodyScenes(plan: MotionPlanResponse | null | undefined): number {
  return (plan?.scenes || []).filter((s) => s.kind === 'body').length;
}

/**
 * 全編を 1 本で作れない理由 (null = 作れる)。GET /motion/plan の wholeAllowed / durationSec / credits から決める。
 * backend が正本 (受付時に replan し、400 / 422 で止める)。ここはボタンの無効化と説明だけ。
 * BGM の有無は見ない: BGM が無くても backend がナレーションの長さと予定尺で秒に割り付ける (2026-09-30 利用者要望)。
 */
export function wholeMotionBlockReason(input: {
  plan: MotionPlanResponse | null | undefined;
}): string | null {
  const { plan } = input;
  if (!plan) return '尺の割付を読み込み中です';
  if (plan.wholeAllowed === true) {
    if (plan.credits && plan.credits.whole == null) return 'モーショングラフィックスの料金が設定されていないため、現在ご利用いただけません';
    return null;
  }
  const dur = plan.durationSec;
  if (typeof dur === 'number' && dur > MOTION_WHOLE_MAX_SEC) {
    return `全編を 1 本で作れるのは ${MOTION_WHOLE_MAX_SEC} 秒までです (尺の割付 ${fmtSec(dur)} 秒)。シーンを減らすか尺を短くしてください`;
  }
  const mp = plan.motion_plan;
  if (!mp || mp.planned !== true) {
    return '尺の割付がありません。「尺の割付を更新」を押してから生成してください';
  }
  if (plan.wholeAllowed === undefined) return '全編の生成に対応していないサーバーです';
  return '全編を 1 本で作れません。「尺の割付を更新」でやり直してください';
}

/** 全編の素材の記録 (GET /motion/plan の whole > シナリオの generation_metadata.motion_whole)。無ければ null。 */
export function readMotionWhole(
  scenario: ScenarioMetaSource | null | undefined,
  plan?: MotionPlanResponse | null,
): MotionWholeInfo | null {
  if (plan && plan.whole !== undefined) {
    return plan.whole && typeof plan.whole.materialId === 'string' && plan.whole.materialId ? plan.whole : null;
  }
  const raw = metaOf(scenario).motion_whole;
  if (!raw || typeof raw !== 'object') return null;
  const w = raw as Record<string, unknown>;
  if (typeof w.materialId !== 'string' || !w.materialId) return null;
  return {
    materialId: w.materialId,
    planVersion: typeof w.planVersion === 'string' ? w.planVersion : null,
    createdAt: typeof w.createdAt === 'string' ? w.createdAt : null,
    jobId: typeof w.jobId === 'string' ? w.jobId : null,
  };
}

/** 全編ジョブの完了結果 → トーストの文言と、画面に反映するシーンの一覧。 */
export function summarizeWholeMotionResult(
  result: Partial<MotionWholeJobResult> | null | undefined,
  kind: 'generate' | 'revise',
): { message: string; passed: boolean | null; scenes: MotionWholeJobResult['scenes'] } {
  const scenes = Array.isArray(result?.scenes) ? result!.scenes : [];
  const head = kind === 'revise' ? '全編のモーションを AI で修正しました' : '全編のモーションを生成しました';
  const parts: string[] = [];
  if (scenes.length > 0) parts.push(`${scenes.length} シーンに分割`);
  if (typeof result?.duration_sec === 'number') parts.push(`${fmtSec(result.duration_sec)} 秒`);
  const round = typeof result?.adopted_round === 'number' ? result.adopted_round : null;
  const passed = typeof result?.adopted_passed === 'boolean' ? result.adopted_passed : null;
  if (round != null) {
    parts.push(passed === false ? `第 ${round} 版を採用・採点は不合格 (最も点の高い版)` : `第 ${round} 版を採用`);
  } else if (passed === false) {
    parts.push('採点は不合格 (最も点の高い版を採用)');
  }
  return { message: parts.length > 0 ? `${head} (${parts.join('・')})` : head, passed, scenes };
}

/** 全編の生成 / 修正の受付エラーの分類 (API の応答 → 画面の扱い)。 */
export type WholeMotionErrorAction =
  | { kind: 'purchase'; message: string }
  | { kind: 'advice'; message: string; advice: MotionPlanAdvice }
  | { kind: 'retryable'; message: string }
  | { kind: 'running'; jobId: string; message: string }
  | { kind: 'message'; message: string };

export const MOTION_ANALYSIS_RETRYABLE_MESSAGE = '解析サービスに接続できません。復旧後にもう一度お試しください';

export function classifyWholeMotionError(
  status: number | null | undefined,
  data: {
    error?: string; error_code?: string; reason?: string; advice?: MotionPlanAdvice; retryable?: boolean; job_id?: string;
    scenes?: Array<{ order?: number | null; reason?: string | null }>;
  } | null | undefined,
  fallback: string,
): WholeMotionErrorAction {
  const d = data || {};
  // 声が映像の生成時から変わった (Phase 2g・§10.7.4): AI で修正は受け付けない。全編の生成し直しを促す
  const voiceStale = motionVoiceStaleMessage(d);
  if (voiceStale) return { kind: 'message', message: voiceStale };
  if (status === 402 || d.error_code === 'INSUFFICIENT_CREDITS') {
    return { kind: 'purchase', message: d.error || 'ポイントが不足しています' };
  }
  if (status === 409 && d.job_id) {
    return { kind: 'running', jobId: d.job_id, message: d.error || '実行中のジョブがあります' };
  }
  if (status === 422) {
    if (d.retryable) return { kind: 'retryable', message: MOTION_ANALYSIS_RETRYABLE_MESSAGE };
    if (d.advice) return { kind: 'advice', message: d.advice.message || d.reason || d.error || fallback, advice: d.advice };
    return { kind: 'message', message: d.reason || d.error || fallback };
  }
  if (status === 503) {
    return { kind: 'message', message: d.error || 'モーショングラフィックスの料金が設定されていないため、現在ご利用いただけません' };
  }
  return { kind: 'message', message: d.error || fallback };
}

/** 全編の「AI で修正」の指示の検証 (null = 送れる)。 */
export function validateMotionReviseInstruction(text: string): string | null {
  const t = text.trim();
  if (!t) return '修正の指示を入力してください';
  if (t.length > MOTION_REVISE_INSTRUCTION_MAX) return `修正の指示は ${MOTION_REVISE_INSTRUCTION_MAX} 文字以内で入力してください`;
  return null;
}

/** 想定尺 option の秒数 (metadata.seconds > ラベルの「N 分 M 秒」)。読めなければ null。 */
export function durationOptionSeconds(opt: { label?: string; metadata?: Record<string, unknown> | null } | null | undefined): number | null {
  if (!opt) return null;
  const meta = opt.metadata?.seconds;
  if (typeof meta === 'number' && Number.isFinite(meta)) return meta;
  const label = opt.label ?? '';
  const min = /(\d+)\s*分/.exec(label);
  const sec = /(\d+)\s*秒/.exec(label);
  if (!min && !sec) return null;
  return (min ? Number(min[1]) * 60 : 0) + (sec ? Number(sec[1]) : 0);
}

/** 想定尺 option が全編モーションの上限 (60 秒) を超えるか (秒数が読めない option は対象外 = false)。 */
export function isDurationOverMotionWholeMax(opt: { label?: string; metadata?: Record<string, unknown> | null } | null | undefined): boolean {
  const s = durationOptionSeconds(opt);
  return s != null && s > MOTION_WHOLE_MAX_SEC;
}

/**
 * 映像スタイルがモーションのときの想定尺の選択肢: 60 秒超を外す (§10.6)。
 * 既に選ばれている値は表示名が消えないように残す (呼び出し側が警告を出して送らせない)。
 */
export function filterDurationOptionsForMotion<T extends { value: string; label: string; metadata?: Record<string, unknown> | null }>(
  options: readonly T[],
  currentValue: string | null | undefined,
): T[] {
  return options.filter((o) => o.value === currentValue || !isDurationOverMotionWholeMax(o));
}

/** 全編モーションで作れるシナリオの本数。BGM先行フロー (/scenarios/motion/prepare) がシェル1本+BGM1本で
 *  count=1に固定しているため (計画md「複数本のシナリオ (未対応・要判断)」)、設定欄でも1案に固定する。 */
export const MOTION_SCENARIO_COUNT = 1;

/** 映像スタイルをモーショングラフィックスにしたとき、シナリオ数を1案に強制した旨のトースト文。 */
export const MOTION_SCENARIO_COUNT_FORCED_MESSAGE =
  'モーショングラフィックスでは1案ずつ作成するため、シナリオ数を1案に変更しました';

/** モーショングラフィックスのときのシナリオ数の選択肢 (1案だけ)。それ以外はそのまま返す。 */
export function filterScenarioCountOptionsForMotion(options: readonly number[], isMotionEngine: boolean): number[] {
  if (!isMotionEngine) return [...options];
  return [MOTION_SCENARIO_COUNT];
}

/** 想定尺が 60 秒を超えていて全編を 1 本で作れないときの警告文。 */
export const MOTION_DURATION_TOO_LONG_MESSAGE =
  `モーショングラフィックスで全編を 1 本で作れるのは ${MOTION_WHOLE_MAX_SEC} 秒までです。想定尺を ${MOTION_WHOLE_MAX_SEC} 秒以内にしてください`;

/** BGM 先行の確認画面: 曲の実尺が 60 秒を超えるときの注意 (生成は止めない)。秒数が分からなければ null。 */
export function motionWholeBgmTooLongNotice(songSec: number | null | undefined): string | null {
  if (typeof songSec !== 'number' || !Number.isFinite(songSec) || songSec <= MOTION_WHOLE_MAX_SEC) return null;
  // 文言は利用者指定 (2026-10-05)。曲の実尺は BGM カードの曲の要約に出るのでここでは繰り返さない
  return `モーショングラフィックスの動画を1本で生成できるのは${MOTION_WHOLE_MAX_SEC}秒までです。`;
}

/** 全編を生成済みのシナリオで個別のモーションを作り直すときの確認文。 */
export const MOTION_SCENE_REGENERATE_AFTER_WHOLE_NOTE = 'このシーンだけ作り直します (全編の一貫性は保たれないことがあります)';
